/**
 * React Query hooks for AI features
 * - always call the aiService (which itself calls the aiAPI client)
 * - no direct HTTP here
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import {
  checkAIHealth,
  generateQuiz,
  generateClozeQuiz,
  generateSummary,
  generateExercisePdf,
  generateQAPdf,
  gradeCopy,
} from '@sphere/ai/data-access';
import {
  SupportedLanguage,
  QuizSchema,
  ClozeQuizSchema,
  SummarySchema,
  AIHealthResponse,
  GradeResult,
} from '@sphere/ai/domain';
import { useState } from 'react';
import { validateFile } from '@sphere/shared/util';

// --------------------
// Query Keys
// --------------------
export const AI_QUERY_KEYS = {
  health: ['ai', 'health'] as const,
  quiz: (fileId: string) => ['ai', 'quiz', fileId] as const,
  clozeQuiz: (fileId: string) => ['ai', 'cloze-quiz', fileId] as const,
  summary: (fileId: string) => ['ai', 'summary', fileId] as const,
  exercise: (fileId: string) => ['ai', 'exercise', fileId] as const,
  qa: (fileId: string) => ['ai', 'qa', fileId] as const,
  gradeCopy: ['ai', 'grade-copy'] as const,
} as const;

// Common vars for mutations
type FileLangVars = { file: File; lang?: SupportedLanguage };

// --------------------
// Health
// --------------------
export function useAIHealth() {
  return useQuery<AIHealthResponse>({
    queryKey: AI_QUERY_KEYS.health,
    queryFn: checkAIHealth,
    staleTime: 30_000, // 30s
    refetchInterval: 60_000, // 1min
    retry: 3,
  });
}

// --------------------
// Generate Quiz (MCQ)
// --------------------
export function useGenerateQuiz() {
  const [fileInfo, setFileInfo] = useState<{
    name: string;
    size: number;
  } | null>(null);

  return useMutation<QuizSchema, Error, FileLangVars>({
    mutationFn: ({ file, lang }) => {
      setFileInfo({ name: file.name, size: file.size });
      return generateQuiz(file, lang ?? SupportedLanguage.FR);
    },
    onError: (error) => {
      console.error('Quiz generation failed:', error);
    },
    meta: { fileInfo },
  });
}

// --------------------
// Generate Cloze Quiz
// --------------------
export function useGenerateClozeQuiz() {
  const [fileInfo, setFileInfo] = useState<{
    name: string;
    size: number;
  } | null>(null);

  return useMutation<ClozeQuizSchema, Error, FileLangVars>({
    mutationFn: ({ file, lang }) => {
      setFileInfo({ name: file.name, size: file.size });
      return generateClozeQuiz(file, lang ?? SupportedLanguage.FR);
    },
    onError: (error) => {
      console.error('Cloze quiz generation failed:', error);
    },
    meta: { fileInfo },
  });
}

// --------------------
// Generate Summary
// --------------------
export function useGenerateSummary() {
  const [fileInfo, setFileInfo] = useState<{
    name: string;
    size: number;
  } | null>(null);

  return useMutation<SummarySchema, Error, FileLangVars>({
    mutationFn: ({ file, lang }) => {
      setFileInfo({ name: file.name, size: file.size });
      return generateSummary(file, lang ?? SupportedLanguage.FR);
    },
    onError: (error) => {
      console.error('Summary generation failed:', error);
    },
    meta: { fileInfo },
  });
}

// --------------------
// Generate & download Exercise PDF
// (service already downloads + returns { blob, filename })
// --------------------
export function useGenerateExercisePdf() {
  return useMutation<{ blob: Blob; filename: string }, Error, FileLangVars>({
    mutationFn: async ({ file, lang }) => {
      return generateExercisePdf({ file, lang: lang ?? SupportedLanguage.FR });
    },
    onError: (error) => {
      console.error('Exercise PDF generation failed:', error);
    },
  });
}

// --------------------
// Generate & download Q&A PDF
// (service already downloads + returns the blob)
// --------------------
export function useGenerateQAPdf() {
  return useMutation<Blob, Error, FileLangVars>({
    mutationFn: async ({ file, lang }) => {
      return generateQAPdf(file, lang ?? SupportedLanguage.FR);
    },
    onError: (error) => {
      console.error('Q&A PDF generation failed:', error);
    },
  });
}

// --------------------
// Grade Copy
// --------------------

type GradeCopyVars = {
  answerKey: File;
  studentCopy: File;
  totalPoints: number;
  lang?: SupportedLanguage;
};

export function useGradeCopy() {
  return useMutation<GradeResult, Error, GradeCopyVars>({
    mutationFn: ({ answerKey, studentCopy, totalPoints, lang }) =>
      gradeCopy(
        answerKey,
        studentCopy,
        totalPoints,
        lang ?? SupportedLanguage.FR,
      ),
    onError: (error) => {
      console.error('Copy grading failed:', error);
    },
  });
}

// --------------------
// Composite hook
// --------------------
export function useAI() {
  const health = useAIHealth();
  const generateQuiz = useGenerateQuiz();
  const generateClozeQuiz = useGenerateClozeQuiz();
  const generateSummary = useGenerateSummary();
  const generateExercisePdf = useGenerateExercisePdf();
  const generateQAPdf = useGenerateQAPdf();
  const gradeCopyMutation = useGradeCopy();

  return {
    health,
    quiz: generateQuiz,
    clozeQuiz: generateClozeQuiz,
    summary: generateSummary,
    exercisePdf: generateExercisePdf,
    qaPdf: generateQAPdf,
    gradeCopy: gradeCopyMutation,

    // Useful aggregate states
    isAnyLoading:
      generateQuiz.isPending ||
      generateClozeQuiz.isPending ||
      generateSummary.isPending ||
      generateExercisePdf.isPending ||
      generateQAPdf.isPending ||
      gradeCopyMutation.isPending,

    hasAnyError:
      generateQuiz.isError ||
      generateClozeQuiz.isError ||
      generateSummary.isError ||
      generateExercisePdf.isError ||
      generateQAPdf.isError ||
      gradeCopyMutation.isError,
  };
}

// --------------------
// File validation with local state
// --------------------
export function useFileValidation() {
  const [file, setFile] = useState<File | null>(null);
  const [validation, setValidation] = useState<{
    isValid: boolean;
    error?: string;
  }>({
    isValid: true,
  });

  const validateAndSetFile = (newFile: File | null) => {
    setFile(newFile);
    if (newFile) {
      const result = validateFile(newFile);
      setValidation(result);
    } else {
      setValidation({ isValid: true });
    }
  };

  return {
    file,
    validation,
    setFile: validateAndSetFile,
    isValid: validation.isValid,
    error: validation.error,
  };
}
